import React, { useState, useEffect, useRef, useCallback } from "react";
import HttpRequest from "../../../HttpService";
import { useUserContext} from "../../../lib/user_context";
import { Box } from "@mui/material";

const sxContainer = {
  display: "flex",
  position: "relative",
  width: "100%",
  border: 0,
  height: '100%',
};
const sxWrapper = {
  display: 'flex',
  height: '100%',
  width: '100%',
  position: 'relative',
};
const sxRouteSelector = {
  position: 'absolute',
  top: '10px',
  right: '10px',
  zIndex: 10,
  backgroundColor: 'white',
  padding: '5px',
  borderRadius: '4px',
  boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
  display: 'flex',
  flexDirection: 'column',
  gap: '5px',
};
const sxRouteButton = {
  padding: '5px 10px',
  border: '1px solid #ccc',
  borderRadius: '3px',
  backgroundColor: '#f8f8f8',
  cursor: 'pointer',
  fontSize: '12px',
  '&:hover': {
    backgroundColor: '#e8e8e8',
  },
  '&.active': {
    backgroundColor: '#4285f4',
    color: 'white',
    borderColor: '#3367d6',
  },
};

// Create a debounced function - defined outside the component
function createDebouncedFunction(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const MapContainer = (props) => {
  const { sessionLoginUser, sessionAccount, sessionAccountAddress } = useUserContext();
  const [accountAddress, setAccountAddress] = useState(null);
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const directionsServiceRef = useRef(null);
  const directionsRenderersRef = useRef([]);
  const streetViewServiceRef = useRef(null);
  const contextMenuRef = useRef(null);

  const [mapLoadError, setMapLoadError] = useState(false);
  const scriptLoadingRef = useRef(false);
  const retryTimeoutRef = useRef(null);
  const [activeRoute, setActiveRoute] = useState(0);
  const [routes, setRoutes] = useState([]);
  const routeColors = ['#4285F4', '#EA4335', '#FBBC05'];
  const routeNames = ['Fastest', 'Shortest', 'Alternative'];
  const shortestRouteDistanceRef = useRef(null);

  // Store prev coordinates to prevent unnecessary re-renders
  const prevCoordsRef = useRef({
    consign: null,
    shipper: null
  });
  
  // Process route results and update UI
  const processRouteResults = useCallback((result, status) => {
    if (status === 'OK' && result.routes && result.routes.length > 0) {
      // Set routes state
      setRoutes(result.routes);

      // Find the shortest route
      const shortestRoute = result.routes.reduce((prev, curr) => {
        const prevDistance = prev.legs[0].distance.value;
        const currDistance = curr.legs[0].distance.value;
        return currDistance < prevDistance ? curr : prev;
      });

      // Get the distance in meters
      const shortestDistance = shortestRoute.legs[0].distance.value;
      
      // Only update if it changed
      if (shortestRouteDistanceRef.current !== shortestDistance) {
        shortestRouteDistanceRef.current = shortestDistance;
        
        // Notify parent component about the shortest distance
        if (props.onRoutesLoaded && typeof props.onRoutesLoaded === 'function') {
          props.onRoutesLoaded({ distance: shortestDistance });
        }
      }

      // Clear existing renderers first
      directionsRenderersRef.current.forEach(renderer => {
        if (renderer) renderer.setMap(null);
      });
      directionsRenderersRef.current = [];

      // Create a renderer for each route (max 3)
      result.routes.slice(0, 3).forEach((route, index) => {
        try {
          const renderer = new window.google.maps.DirectionsRenderer({
            map: googleMapRef.current,
            directions: result,
            routeIndex: index,
            polylineOptions: {
              strokeColor: routeColors[index % routeColors.length],
              strokeWeight: index === activeRoute ? 9 : 5,
              strokeOpacity: index === activeRoute ? 1.0 : 0.6
            },
            suppressMarkers: index !== activeRoute
          });
          
          directionsRenderersRef.current.push(renderer);
        } catch (error) {
          console.error("Error creating renderer for route", index, error);
        }
      });
    } else if (status !== 'OK') {
      console.error("Route calculation failed:", status);
    }
  }, [activeRoute, props.onRoutesLoaded, routeColors]);

  // Debounced route request function - created once and persisted
  const debouncedRequestRouteRef = useRef(null);
  
  // Initialize the debounced function once
  useEffect(() => {
    debouncedRequestRouteRef.current = createDebouncedFunction((request) => {
      if (!directionsServiceRef.current || !googleMapRef.current) return;
      
      console.log("Requesting new directions from Google Maps API...", request);

      directionsServiceRef.current.route(request, (result, status) => {
        processRouteResults(result, status);
      });
    }, 500);
  }, [processRouteResults]);

  // Add global context menu prevention
  useEffect(() => {
    const preventContextMenu = (e) => {
      if (contextMenuRef.current) {
        e.preventDefault();
      }
    };
    
    document.addEventListener('contextmenu', preventContextMenu);
    return () => document.removeEventListener('contextmenu', preventContextMenu);
  }, []);

  // Load Google Maps script
  useEffect(() => {
    let scriptElement = document.getElementById('google-maps-script');
    
    const initMapWhenReady = () => {
      if (window.google && window.google.maps && mapRef.current && !googleMapRef.current) {
        initializeMap();
      } else if (mapRef.current) {
        // If map div exists but Google isn't ready, retry in 100ms
        setTimeout(initMapWhenReady, 100);
      }
    };

    const loadGoogleMapsScript = () => {
      if (scriptLoadingRef.current) return;
      
      if (!window.google) {
        scriptLoadingRef.current = true;
        scriptElement = document.createElement('script');
        scriptElement.id = 'google-maps-script';
        scriptElement.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API_KEY}&libraries=places`;
        scriptElement.async = true;
        
        scriptElement.onload = () => {
          scriptLoadingRef.current = false;
          initMapWhenReady();
        };

        scriptElement.onerror = () => {
          scriptLoadingRef.current = false;
          setMapLoadError(true);
          // Retry after 3 seconds
          retryTimeoutRef.current = setTimeout(loadGoogleMapsScript, 3000);
        };

        document.head.appendChild(scriptElement);
      } else {
        initMapWhenReady();
      }
    };

    if (document.readyState === 'complete') {
      loadGoogleMapsScript();
    } else {
      window.addEventListener('load', loadGoogleMapsScript);
    }

    // Cleanup function
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (contextMenuRef.current && document.body.contains(contextMenuRef.current)) {
        document.body.removeChild(contextMenuRef.current);
      }
      // Remove script if we added it
      if (scriptElement && document.head.contains(scriptElement)) {
        document.head.removeChild(scriptElement);
      }
      window.removeEventListener('load', loadGoogleMapsScript);
    };
  }, []);

  // Load user address
  useEffect(() => {
    if (sessionLoginUser && sessionAccount && props.accountNumber) {
      var userAddressID = sessionLoginUser?.address?.addressID;
      if (userAddressID) {
        HttpRequest.getAddressByID(props.accountNumber, userAddressID).then(
          async (body) => {
            if (body?.data?.[0]) {
              localStorage.setItem("userAddress", JSON.stringify(body.data[0]));
              setAccountAddress(body.data[0]);
            }
          }
        ).catch(err => {
          console.error("Error getting address:", err);
        });
      }
    }
  }, [sessionLoginUser, sessionAccount, props.accountNumber]);

  // Address select event handler
  const markerRef = useRef(null);
  useEffect(() => {
    const handleAddressSelect = (event) => {
      if (!googleMapRef.current) return;
      
      const { lat, lng } = event.detail;
      
      // Clear existing marker if any
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }

      // Create new marker
      markerRef.current = new window.google.maps.Marker({
        position: { lat, lng },
        map: googleMapRef.current,
        animation: window.google.maps.Animation.DROP
      });

      // Center map and zoom
      googleMapRef.current.setCenter({ lat, lng });
      googleMapRef.current.setZoom(16);

      if (props.type === "place") {
        streetViewServiceRef.current = new window.google.maps.StreetViewService();
        const panorama = new window.google.maps.StreetViewPanorama(mapRef.current, {
          position: { lat, lng },
          pov: { heading: 210, pitch: 10 },
          zoom: 1
        });
        googleMapRef.current.setStreetView(panorama);
      }
    };
    
    const handleClearMarker = () => {
        if (markerRef.current) {
            markerRef.current.setMap(null);
        }
    };

    window.addEventListener('addressSelected', handleAddressSelect);
    window.addEventListener('clearPreviewMarker', handleClearMarker);

    return () => {
        window.removeEventListener('addressSelected', handleAddressSelect);
        window.removeEventListener('clearPreviewMarker', handleClearMarker);
    }
  }, [props.type]);

  // Handle map updates with coordinates
  useEffect(() => {
    if (!googleMapRef.current || !directionsServiceRef.current || !debouncedRequestRouteRef.current) {
      return;
    }
    
    // Parse and validate coordinates
    const getValidCoord = (coord, defaultLat, defaultLng) => {
      if (coord && coord.lat && coord.long && 
          !isNaN(parseFloat(coord.lat)) && !isNaN(parseFloat(coord.long))) {
        return { 
          lat: parseFloat(coord.lat), 
          lng: parseFloat(coord.long) 
        };
      }
      
      // Try to use session address as fallback
      if (sessionAccountAddress && sessionAccountAddress !== "") {
        try {
          const address = typeof sessionAccountAddress === 'string' 
            ? JSON.parse(sessionAccountAddress) 
            : sessionAccountAddress;
            
          if (address && address.lat && address.long) {
            return { 
              lat: parseFloat(address.lat), 
              lng: parseFloat(address.long) 
            };
          }
        } catch (e) {
          console.error("Error parsing session address:", e);
        }
      }
      
      // Default coordinates
      return { lat: defaultLat, lng: defaultLng };
    };

    // Default center of the map
    const defaultLat = 51.052224;
    const defaultLng = -114.068237;
    
    // Get validated coordinates
    const consignCoord = getValidCoord(props.consignCoord, defaultLat, defaultLng);
    const shipperCoord = getValidCoord(props.shipperCoord, defaultLat, defaultLng);

    // Check if coordinates actually changed
    const prevConsign = prevCoordsRef.current.consign;
    const prevShipper = prevCoordsRef.current.shipper;
    
    const coordsChanged = !prevConsign || !prevShipper ||
      Math.abs(prevConsign.lat - consignCoord.lat) > 0.00001 || 
      Math.abs(prevConsign.lng - consignCoord.lng) > 0.00001 ||
      Math.abs(prevShipper.lat - shipperCoord.lat) > 0.00001 || 
      Math.abs(prevShipper.lng - shipperCoord.lng) > 0.00001;
      
    // If coordinates didn't actually change, skip the update
    if (!coordsChanged) {
      return;
    }

    prevCoordsRef.current = {
      consign: {...consignCoord},
      shipper: {...shipperCoord}
    };

    // Clear existing street view
    if (streetViewServiceRef.current && googleMapRef.current.getStreetView()) {
      googleMapRef.current.setStreetView(null);
    }

    // Handle street view mode
    if (props.type === "place") {
      // Clear existing renderers
      directionsRenderersRef.current.forEach(renderer => {
        if (renderer) renderer.setMap(null);
      });
      directionsRenderersRef.current = [];
      
      // Set up street view
      try {
        streetViewServiceRef.current = new window.google.maps.StreetViewService();
        const panorama = new window.google.maps.StreetViewPanorama(mapRef.current, {
          position: consignCoord,
          pov: { heading: 210, pitch: 10 },
          zoom: 1
        });
        googleMapRef.current.setStreetView(panorama);
      } catch (error) {
        console.error("Error setting street view:", error);
      }
      return;
    }
    
    // Handle directions mode
    const isSameLocation = 
      Math.abs(shipperCoord.lat - consignCoord.lat) < 0.0001 && 
      Math.abs(shipperCoord.lng - consignCoord.lng) < 0.0001;
    
    if (!props.consignAddress || !props.shipperAddress || isSameLocation) {
      // Just show the map centered on a location without directions
      directionsRenderersRef.current.forEach(renderer => {
        if (renderer) renderer.setMap(null);
      });
      directionsRenderersRef.current = [];
      setRoutes([]);
      
      googleMapRef.current.setCenter(shipperCoord);
      googleMapRef.current.setZoom(13);
      return;
    }
    
    // Request directions with debouncing
    const request = {
      origin: shipperCoord,
      destination: consignCoord,
      travelMode: window.google.maps.TravelMode.DRIVING,
      provideRouteAlternatives: true
    };
    
    // Use debounced request to prevent excessive API calls
    debouncedRequestRouteRef.current(request);
    
  }, [
    props.consignCoord, 
    props.shipperCoord,
    props.consignAddress,
    props.shipperAddress,
    props.type, 
    sessionAccountAddress,
    activeRoute
  ]);

  // Handle route selection
  const handleRouteSelect = (index) => {
    setActiveRoute(index);
    
    // Update route styles
    directionsRenderersRef.current.forEach((renderer, idx) => {
      if (renderer) {
        try {
          renderer.setOptions({
            polylineOptions: {
              strokeColor: routeColors[idx % routeColors.length],
              strokeWeight: idx === index ? 5 : 3,
              strokeOpacity: idx === index ? 1.0 : 0.6
            },
            suppressMarkers: idx !== index
          });
          
          // If it's the selected route, update distance if needed
          if (idx === index && renderer.directions && 
              renderer.directions.routes && 
              renderer.directions.routes[renderer.routeIndex] &&
              renderer.directions.routes[renderer.routeIndex].legs &&
              renderer.directions.routes[renderer.routeIndex].legs[0] &&
              renderer.directions.routes[renderer.routeIndex].legs[0].distance) {
            
            const distance = renderer.directions.routes[renderer.routeIndex].legs[0].distance.value;
            
            // Only notify parent if the distance changed
            if (shortestRouteDistanceRef.current !== distance) {
              shortestRouteDistanceRef.current = distance;
              
              if (props.onRoutesLoaded && typeof props.onRoutesLoaded === 'function') {
                props.onRoutesLoaded({ distance: distance });
              }
            }
          }
        } catch (error) {
          console.error("Error updating renderer options:", error);
        }
      }
    });
  };

  // Utility functions for address form interactions
  const shouldShowOverwriteOption = () => {
    // Check if the address form is open
    const isFormOpen = document.querySelector('.addNewAddressForm') !== null || 
                      document.querySelector('.editAddressForm') !== null;
    
    if (!isFormOpen) return false;
    
    // Check if there's an address in the form
    const addressInput = document.querySelector('input[name="address"]');
    const hasAddress = addressInput && addressInput.value.trim().length > 0;
    
    // Check if the company name field is filled (additional validation)
    const companyInput = document.querySelector('input[name="companyName"]');
    const hasCompany = companyInput && companyInput.value.trim().length > 0;
    
    return isFormOpen && hasAddress && hasCompany;
  };

  const setAddressFormCoordinates = (lat, lng) => {
    try {
      const coordInput = document.querySelector('input[name="latLongString"]');
      if (coordInput) {
        const coordString = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
        coordInput.value = coordString;
        
        // Trigger change event to update the form state
        const event = new Event('input', { bubbles: true });
        coordInput.dispatchEvent(event);
        
        // Create a marker or update existing marker
        if (markerRef.current) {
          markerRef.current.setMap(null);
        }
        
        markerRef.current = new window.google.maps.Marker({
          position: { lat, lng },
          map: googleMapRef.current,
          animation: window.google.maps.Animation.DROP,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: '#4285F4',
            fillOpacity: 1,
            strokeWeight: 0,
            scale: 8
          }
        });
        
        // Add a visual feedback with animation
        const feedbackElement = document.createElement('div');
        feedbackElement.textContent = "Coordinates updated!";
        feedbackElement.style.position = 'absolute';
        feedbackElement.style.top = '10px';
        feedbackElement.style.left = '50%';
        feedbackElement.style.transform = 'translateX(-50%)';
        feedbackElement.style.backgroundColor = 'rgba(66, 133, 244, 0.9)';
        feedbackElement.style.color = 'white';
        feedbackElement.style.padding = '8px 16px';
        feedbackElement.style.borderRadius = '4px';
        feedbackElement.style.zIndex = '1000';
        feedbackElement.style.transition = 'opacity 0.5s ease-in-out';
        
        mapRef.current.appendChild(feedbackElement);
        
        setTimeout(() => {
          feedbackElement.style.opacity = '0';
          setTimeout(() => {
            if (mapRef.current && mapRef.current.contains(feedbackElement)) {
              mapRef.current.removeChild(feedbackElement);
            }
          }, 500);
        }, 2000);
      }
    } catch (error) {
      console.error('Error updating coordinates in address form:', error);
    }
  };

  const removeContextMenu = () => {
    if (contextMenuRef.current && document.body.contains(contextMenuRef.current)) {
      document.body.removeChild(contextMenuRef.current);
    }
    contextMenuRef.current = null;
    document.removeEventListener('click', closeMenu);
    document.removeEventListener('contextmenu', closeMenu);
  };

  const closeMenu = (event) => {
    if (!contextMenuRef.current || !contextMenuRef.current.contains(event.target)) {
      removeContextMenu();
    }
  };

  // Initialize Google Maps
  const initializeMap = () => {
    const defaultCenter = { lat: 51.052224, lng: -114.068237 };
    
    googleMapRef.current = new window.google.maps.Map(mapRef.current, {
      zoom: 15,
      center: defaultCenter,
      mapTypeId: window.google.maps.MapTypeId.ROADMAP,
      streetViewControl: true,
      clickableIcons: false // Disable default POI markers
    });

    directionsServiceRef.current = new window.google.maps.DirectionsService();

    // Add right-click listener for coordinate menu
    googleMapRef.current.addListener('rightclick', (e) => {
        e.domEvent.preventDefault(); // Prevent default context menu
        e.domEvent.stopPropagation(); // Stop event bubbling

        // Remove existing context menu if it exists
        removeContextMenu();

        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        const coordString = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

        const contextMenu = document.createElement('div');
        contextMenuRef.current = contextMenu;
        contextMenu.className = 'custom-map-context-menu';
        contextMenu.style.position = 'fixed';
        contextMenu.style.zIndex = '1000';
        contextMenu.style.backgroundColor = 'white';
        contextMenu.style.boxShadow = '0 2px 5px rgba(0,0,0,0.2)';
        contextMenu.style.padding = '5px 0';
        contextMenu.style.borderRadius = '4px';
        contextMenu.style.left = e.domEvent.clientX + 'px';
        contextMenu.style.top = e.domEvent.clientY + 'px';

        // Create menu styles
        const menuItemStyle = {
            padding: '8px 16px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        };

        // Add "Overwrite GPS Coordinates" option if address form is open and there's an existing address
        if (shouldShowOverwriteOption()) {
            const overwriteButton = document.createElement('div');
            Object.assign(overwriteButton.style, menuItemStyle);
            overwriteButton.innerHTML = `<span style="color:#EA4335">📍</span> Overwrite GPS Coordinates`;
            overwriteButton.onmouseover = () => { overwriteButton.style.backgroundColor = '#f0f0f0'; };
            overwriteButton.onmouseout = () => { overwriteButton.style.backgroundColor = 'white'; };
            overwriteButton.onclick = () => {
                // Create and show confirmation dialog
                const confirmDialog = document.createElement('div');
                confirmDialog.style.position = 'fixed';
                confirmDialog.style.top = '0';
                confirmDialog.style.left = '0';
                confirmDialog.style.right = '0';
                confirmDialog.style.bottom = '0';
                confirmDialog.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
                confirmDialog.style.zIndex = '2000';
                confirmDialog.style.display = 'flex';
                confirmDialog.style.justifyContent = 'center';
                confirmDialog.style.alignItems = 'center';
                
                const dialogBox = document.createElement('div');
                dialogBox.style.backgroundColor = 'white';
                dialogBox.style.borderRadius = '8px';
                dialogBox.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                dialogBox.style.padding = '20px';
                dialogBox.style.maxWidth = '320px';
                dialogBox.style.textAlign = 'center';
                
                const title = document.createElement('h3');
                title.style.margin = '0 0 16px 0';
                title.style.fontSize = '18px';
                title.style.color = '#202124';
                title.textContent = 'Overwrite GPS Coordinates?';
                
                const message = document.createElement('p');
                message.style.margin = '0 0 20px 0';
                message.style.fontSize = '14px';
                message.style.color = '#5f6368';
                message.style.lineHeight = '1.5';
                message.textContent = 'This will update the location coordinates without changing the address text. The marker will move to this new position.';
                
                const coordDisplay = document.createElement('div');
                coordDisplay.style.backgroundColor = '#f8f9fa';
                coordDisplay.style.padding = '8px';
                coordDisplay.style.borderRadius = '4px';
                coordDisplay.style.marginBottom = '20px';
                coordDisplay.style.fontSize = '14px';
                coordDisplay.style.fontFamily = 'monospace';
                coordDisplay.textContent = coordString;
                
                const buttonContainer = document.createElement('div');
                buttonContainer.style.display = 'flex';
                buttonContainer.style.justifyContent = 'space-between';
                
                const cancelButton = document.createElement('button');
                cancelButton.style.padding = '8px 16px';
                cancelButton.style.backgroundColor = '#f8f9fa';
                cancelButton.style.color = '#3c4043';
                cancelButton.style.border = '1px solid #dadce0';
                cancelButton.style.borderRadius = '4px';
                cancelButton.style.fontSize = '14px';
                cancelButton.style.fontWeight = '500';
                cancelButton.style.cursor = 'pointer';
                cancelButton.textContent = 'Cancel';
                cancelButton.onmouseover = () => { cancelButton.style.backgroundColor = '#f1f3f4'; };
                cancelButton.onmouseout = () => { cancelButton.style.backgroundColor = '#f8f9fa'; };
                
                const confirmButton = document.createElement('button');
                confirmButton.style.padding = '8px 24px';
                confirmButton.style.backgroundColor = '#4285f4';
                confirmButton.style.color = 'white';
                confirmButton.style.border = 'none';
                confirmButton.style.borderRadius = '4px';
                confirmButton.style.fontSize = '14px';
                confirmButton.style.fontWeight = '500';
                confirmButton.style.cursor = 'pointer';
                confirmButton.textContent = 'Yes, Update';
                confirmButton.onmouseover = () => { confirmButton.style.backgroundColor = '#3b78e7'; };
                confirmButton.onmouseout = () => { confirmButton.style.backgroundColor = '#4285f4'; };
                
                buttonContainer.appendChild(cancelButton);
                buttonContainer.appendChild(confirmButton);
                
                dialogBox.appendChild(title);
                dialogBox.appendChild(message);
                dialogBox.appendChild(coordDisplay);
                dialogBox.appendChild(buttonContainer);
                confirmDialog.appendChild(dialogBox);
                
                document.body.appendChild(confirmDialog);
                
                const closeDialog = () => {
                    if (document.body.contains(confirmDialog)) {
                        document.body.removeChild(confirmDialog);
                    }
                };
                
                // Handle dialog button clicks
                cancelButton.onclick = () => {
                    closeDialog();
                };
                
                confirmButton.onclick = () => {
                    closeDialog();
                    setAddressFormCoordinates(lat, lng);
                };
                
                // Close dialog when clicking outside
                confirmDialog.onclick = (e) => {
                    if (e.target === confirmDialog) {
                        closeDialog();
                    }
                };
                
                removeContextMenu();
            };
            
            contextMenu.appendChild(overwriteButton);
            document.body.appendChild(contextMenu);

            // Use requestAnimationFrame to ensure the menu is rendered before adding listeners
            requestAnimationFrame(() => {
                document.addEventListener('click', closeMenu);
                document.addEventListener('contextmenu', closeMenu);
            });
        }
    });
  };

  return (
    <Box sx={sxWrapper}>
      <Box ref={mapRef} sx={sxContainer} />
      {props.type === "directions" && routes.length > 0 && (
        <Box sx={sxRouteSelector}>
          {routes.slice(0, 3).map((route, index) => (
            <Box
              component="button"
              key={index}
              sx={sxRouteButton}
              className={index === activeRoute ? 'active' : ''}
              onClick={() => handleRouteSelect(index)}
              style={{
                borderLeft: `4px solid ${routeColors[index % routeColors.length]}`,
                backgroundColor: index === activeRoute ? '#e7f0fe' : '#fff'
              }}
            >
              {routeNames[index]} {route.legs[0].duration.text}
            </Box>
          ))}
        </Box>
      )}
      {mapLoadError && (
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          padding: '15px',
          borderRadius: '4px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
          textAlign: 'center'
        }}>
          Map failed to load. Retrying...
        </Box>
      )}
    </Box>
  );
};

export default MapContainer;